// @ts-nocheck
// Vendored from tailuge/billiards (GPL-3.0) — src/view/tablemesh.ts
// Modified: import paths point to vendored engine geometry.
// Modified: Phong → PBR (MeshStandardMaterial). Cloth/cushion gain roughness=0.92
// for matte felt look; pocket stays near-black. flatShading off for smooth lighting.
import {
  Vector3,
  Mesh,
  CylinderGeometry,
  BoxGeometry,
  MeshStandardMaterial,
  PointLight,
  Group,
} from "three"
import { TableGeometry } from "../../engine/tablegeometry"
import { PocketGeometry } from "../../engine/pocketgeometry"
import { R } from "../../engine/constants"

export class TableMesh {
  logger = (_) => {}

  static mesh

  private static readonly cylinderCache = new Map<string, CylinderGeometry>()
  private static readonly boxCache = new Map<string, BoxGeometry>()

  generateTable(hasPockets: boolean) {
    const group = new Group()
    const light = new PointLight(0xf0f0e8, 22)
    light.position.set(0, 0, R * 50)
    group.add(light)
    this.addCushions(group, hasPockets)

    if (hasPockets) {
      // Modified: knuckle cylinders and pocket-center cylinders are both omitted.
      // They appeared as isolated black cylinders sticking out of the cushion
      // and around pockets. The visual is now handled by self-coded modules:
      //   - src/render/table-rails.ts (rail with pocket circular holes)
      //   - src/render/table-pockets.ts (flat black disks beneath rail holes)
      // Physics (knuckle / pocket collisions) is unaffected — it uses
      // PocketGeometry data, not these meshes.
    }
    return group
  }

  // PBR felt: matte tournament green. Cloth and cushion share the same hue so
  // they read as a single fabric piece (real tables use one continuous felt).
  // Modified: public so the felt-color theme (src/render/theme.ts) can register it.
  readonly cloth = new MeshStandardMaterial({
    color: 0x1d7a3e,
    roughness: 0.94,
    metalness: 0.0,
    envMapIntensity: 0.22,
  })

  // Cushion is the same felt color, slightly darker to give a soft shadow against the cloth.
  private readonly cushion = new MeshStandardMaterial({
    color: 0x196b36,
    roughness: 0.96,
    metalness: 0.0,
    envMapIntensity: 0.18,
  })

  private readonly pocket = new MeshStandardMaterial({
    color: 0x050505,
    roughness: 0.75,
    metalness: 0.0,
    envMapIntensity: 0.1,
  })

  // ポケット周りのナックル円柱を黒い革風に。実機ではクッション端は黒いレザー
  // カフで包まれている。これにより緑のクロスからポケット入口がはっきり見える。
  private readonly knuckleLeather = new MeshStandardMaterial({
    color: 0x141414,
    roughness: 0.6,
    metalness: 0.0,
    envMapIntensity: 0.18,
  })

  private knuckleCylinder(knuckle, scene, material = this.knuckleLeather) {
    // Modified default material: cloth(green) → knuckleLeather(black). Pocket
    // cylinders are still drawn with the pocket material (passed explicitly).
    const k = this.cylinder(
      knuckle.pos,
      knuckle.radius,
      (R * 0.75) / 0.5,
      scene,
      material
    )
    k.position.setZ((-R * 0.25) / 0.5 / 2)
  }

  private cylinder(pos, radius, depth, scene, material) {
    const key = `${radius}_${depth}`
    let geometry = TableMesh.cylinderCache.get(key)
    if (!geometry) {
      geometry = new CylinderGeometry(radius, radius, depth, 16)
      TableMesh.cylinderCache.set(key, geometry)
    }
    const mesh = new Mesh(geometry, material)
    mesh.position.copy(pos)
    mesh.rotation.x = Math.PI / 2
    scene.add(mesh)
    return mesh
  }

  addCushions(scene, _hasPockets) {
    // Modified: only the cloth slab is drawn here. The straight cushion box
    // walls (6 planes below in upstream) are intentionally omitted because
    // they cannot represent the slanted "jaw" cut at each pocket. The cushion
    // visuals are now produced by src/render/table-cushions.ts as trapezoidal
    // ExtrudeGeometry shapes per rail segment. Physics is unaffected — it uses
    // PocketGeometry / TableGeometry data, not these meshes.
    const th = (R * 10) / 0.5
    this.plane(
      new Vector3(0, 0, -R - th / 2),
      2 * TableGeometry.X,
      2 * TableGeometry.Y,
      th,
      scene,
      this.cloth
    )
  }

  private plane(pos, x, y, z, scene, material = this.cushion) {
    const key = `${x}_${y}_${z}`
    let geometry = TableMesh.boxCache.get(key)
    if (!geometry) {
      geometry = new BoxGeometry(x, y, z)
      TableMesh.boxCache.set(key, geometry)
    }
    const mesh = new Mesh(geometry, material)
    mesh.receiveShadow = true
    mesh.position.copy(pos)
    scene.add(mesh)
  }
}
